import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ShieldCheck } from "lucide-react";
import { CONSULTOR, WA_DEFAULT } from "../../data/site";
import { WhatsAppIcon } from "./WhatsAppIcon";

const LINES = [
  { text: "Seu plano de saúde," },
  { text: "escolhido de acordo" },
  { before: "com a ", accent: "sua necessidade." },
];

const lineWrap = {
  hidden: {},
  show: (i) => ({
    transition: { staggerChildren: 0.14, delayChildren: 0.35 + i * 0.18 },
  }),
};
const line = {
  hidden: { y: "110%" },
  show: {
    y: "0%",
    transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] },
  },
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], [0, 60]);

  return (
    <section id="top" ref={ref} data-testid="hero-section" className="relative overflow-hidden bg-cream">
      <div className="pointer-events-none absolute inset-0 hero-grid opacity-70" />
      <div className="pointer-events-none absolute -right-40 -top-40 h-[560px] w-[560px] rounded-full bg-champagne/10 blur-3xl" />
      <div className="pointer-events-none absolute -left-52 bottom-0 h-[420px] w-[420px] rounded-full bg-wa/[0.06] blur-3xl" />

      <div className="relative mx-auto grid max-w-7xl grid-cols-1 items-center gap-10 px-4 pb-16 pt-28 sm:px-6 lg:grid-cols-12 lg:gap-12 lg:px-8 lg:pb-24 lg:pt-40">
        <div className="lg:col-span-6">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="mb-6 inline-flex items-center gap-2 rounded-full border border-champagne/30 bg-surface px-4 py-1.5"
          >
            <ShieldCheck className="h-3.5 w-3.5 text-champagne-dark" />
            <span className="font-mono text-[11px] font-semibold uppercase tracking-[0.2em] text-champagne-dark">
              Planos regulamentados pela ANS
            </span>
          </motion.div>

          <h1 className="font-display text-4xl font-semibold leading-[1.08] tracking-tight text-ink sm:text-5xl lg:text-[3.6rem]">
            {LINES.map((l, i) => (
              <span key={i} className="block overflow-hidden pb-1">
                <motion.span
                  className="block"
                  variants={lineWrap}
                  custom={i}
                  initial="hidden"
                  animate="show"
                >
                  <motion.span variants={line} className="block">
                    {l.text}
                    {l.accent && (
                      <>
                        {l.before}
                        <em className="font-medium italic text-champagne-dark">{l.accent}</em>
                      </>
                    )}
                  </motion.span>
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.05 }}
            className="mt-5 max-w-md text-base leading-relaxed text-stone-400"
          >
            Planos de saúde para você, seus dependentes e sua empresa — com
            atendimento personalizado do início ao fim.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.25 }}
            className="mt-8"
          >
            <a
              href={WA_DEFAULT}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-whatsapp-cta"
              className="group inline-flex items-center gap-3 rounded-full bg-wa px-7 py-4 text-sm font-bold text-white shadow-[0_12px_40px_-10px_rgba(37,211,102,0.5)] transition-[background-color,transform,box-shadow] duration-300 hover:-translate-y-1 hover:bg-wa-dark hover:shadow-[0_18px_50px_-10px_rgba(37,211,102,0.6)]"
            >
              <WhatsAppIcon className="h-5 w-5 transition-transform duration-300 group-hover:scale-110 group-hover:rotate-6" />
              Quero encontrar meu plano ideal
            </a>
          </motion.div>
        </div>

        <motion.div style={{ y: yImg }} className="lg:col-span-6">
          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: 24 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
          >
            <div className="absolute -inset-3 rounded-[2rem] border border-champagne/30" />
            <div className="relative overflow-hidden rounded-[1.6rem] shadow-2xl shadow-black/50">
              <img
                src="/assets/consultor-retrato.png"
                alt={`${CONSULTOR.name} trabalhando em seu escritório`}
                data-testid="hero-consultant-photo"
                className="w-full"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent" />
            </div>
            <motion.div
              initial={{ opacity: 0, x: 24 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 1.4 }}
              className="absolute -bottom-5 left-4 rounded-2xl border border-white/10 bg-surface/95 p-4 shadow-xl backdrop-blur-xl sm:left-6"
              data-testid="hero-trust-card"
            >
              <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-champagne-dark">
                {CONSULTOR.name}
              </p>
              <p className="mt-1 font-display text-sm font-semibold text-ink">
                Consultor há mais de {CONSULTOR.years} anos
              </p>
              <p className="mt-0.5 text-xs text-stone-500">
                Presencial ou virtual
              </p>
            </motion.div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};
